import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import templateIcon from "../../media/template_plan.png";
import GetData from "../../services/getData";
import TiptapEditor from "../../components/layout/tiptapEditor/TiptapEditor";
import TiptapViewer from "../../components/layout/tiptapViewer/TiptapViewer";

function TreatmentRecommendView() {
    const { t } = useTranslation("templates");
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === "new";

    const [template, setTemplate] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [templateType, setTemplateType] = useState("common"); // 'common' | 'my'
    const [isEditing, setIsEditing] = useState(isNew);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadTemplate = async () => {
        if (isNew) {
            setTemplate(null);
            setTitle("");
            setDescription("");
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await GetData({
                module: "templates",
                block: "recommendations",
                action: "list",
            });

            const list =
                data?.result ??
                data?.request?.result ??
                [];

            const found = (Array.isArray(list) ? list : []).find(
                (t) => String(t?.id?.value ?? t?.id) === String(id),
            );

            if (!found) {
                setError(t("recommendView.notFound"));
                setTemplate(null);
                return;
            }

            setTemplate(found);
            setTitle(found?.title?.value ?? found?.title ?? "");
            setDescription(
                found?.description?.value ?? found?.description ?? "",
            );
            const idUser =
                found?.id_user?.value ?? found?.id_user ?? 0;
            setTemplateType(idUser === 0 ? "common" : "my");
        } catch (err) {
            console.error("Ошибка загрузки шаблона рекомендаций:", err);
            setError(err.message || t("recommendView.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        setIsEditing(id === "new");
    }, [id]);

    useEffect(() => {
        const defaultTitle = isNew
            ? t("recommendView.newTitle")
            : t("recommendView.title");

        document.title = title || defaultTitle;
    }, [isNew, title, t]);

    const handleBack = () => {
        navigate("/templates/recommendation");
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        if (isNew) {
            navigate("/templates/recommendation");
            return;
        }

        setIsEditing(false);
        if (template) {
            setTitle(template?.title?.value ?? template?.title ?? "");
            setDescription(
                template?.description?.value ?? template?.description ?? "",
            );
            const idUser =
                template?.id_user?.value ?? template?.id_user ?? 0;
            setTemplateType(idUser === 0 ? "common" : "my");
        }
    };

    const handleDeleteClick = async () => {
        if (isNew || !id) return;

        const confirmDelete = window.confirm(
            t("recommendView.confirmDelete"),
        );
        if (!confirmDelete) return;

        try {
            setLoading(true);
            await GetData({
                module: "templates",
                block: "recommendations",
                action: "delete",
                id_template: id,
            });

            try {
                const listData = await GetData({
                    module: "templates",
                    block: "recommendations",
                    action: "list",
                });

                const list =
                    listData?.result ??
                    listData?.request?.result ??
                    [];

                const exists = (Array.isArray(list) ? list : []).some(
                    (t) => String(t?.id?.value ?? t?.id) === String(id),
                );

                if (exists) {
                    alert(t("recommendView.deleteFailed"));
                } else {
                    navigate("/templates/recommendation");
                }
            } catch (checkErr) {
                console.error(
                    "Ошибка проверки удаления шаблона рекомендаций:",
                    checkErr,
                );
                alert(
                    t("recommendView.deleteCheckFailed"),
                );
                navigate("/templates/recommendation");
            }
        } catch (err) {
            console.error("Ошибка удаления шаблона рекомендаций:", err);
            alert(t("recommendView.deleteFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!title.trim() || !description.trim()) {
            alert(t("recommendView.needFields"));
            return;
        }

        setSaving(true);
        try {
            const baseRequest = {
                module: "templates",
                block: "recommendations",
            };

            // Общий / мой шаблон (0 / 4 по аналогии с TreatmentPlanTemplateView)
            const idUser = templateType === "common" ? 0 : 4;

            let data;
            if (!isNew && id) {
                data = await GetData({
                    ...baseRequest,
                    action: "update",
                    id_template: id,
                    title: title.trim(),
                    description: description.trim(),
                    id_user: idUser,
                });
            } else {
                data = await GetData({
                    ...baseRequest,
                    action: "create",
                    title: title.trim(),
                    description: description.trim(),
                    id_user: idUser,
                });
            }

            if (data?.status === "success") {
                const savedId =
                    data.result?.id?.value ??
                    data.result?.id ??
                    null;

                if (savedId != null && savedId !== "") {
                    if (isNew) {
                        navigate(`/templates/recommendation/${savedId}`, {
                            replace: true,
                        });
                    }
                    await loadTemplate();
                } else if (!isNew) {
                    await loadTemplate();
                }

                setIsEditing(false);
            } else if (data?.status) {
                alert(data.message || t("recommendView.saveFailed"));
            } else {
                alert(t("recommendView.saveFailed"));
            }
        } catch (err) {
            console.error("Ошибка сохранения шаблона рекомендаций:", err);
            alert(t("recommendView.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    if (loading && !template && !isNew) {
        return (
            <Container fluid="md" className="mt-4">
                {t("state.loading", { ns: "common" })}
            </Container>
        );
    }

    if (error && !isNew) {
        return (
            <Container fluid="md" className="mt-4">
                <div className="text-danger mb-3">
                    {t("error", { ns: "common", message: error })}
                </div>
                <button className="button-default light" onClick={handleBack}>
                    {t("recommendView.backToList")}
                </button>
            </Container>
        );
    }

    const blockButtons = isEditing
        ? [
              {
                  label: t("actions.save", { ns: "common" }),
                  onClick: handleSave,
                  type: "normal",
              },
              {
                  label: t("actions.cancel", { ns: "common" }),
                  onClick: handleCancelEdit,
                  type: "light",
              },
              {
                  label: t("actions.delete", { ns: "common" }),
                  onClick: handleDeleteClick,
                  type: "danger",
              },
          ]
        : [
              ...(isNew
                  ? []
                  : [
                        {
                            label: (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                  <svg
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    width="20"
                                    height="20"
                                    style={{ fill: "currentColor" }}>
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                                  </svg>
                                  {t("actions.edit", { ns: "common" })}
                                </span>
                              ),
                            onClick: handleEditClick,
                            type: "light icon",
                        },
                    ]),
              {
                  label: (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        width="20"
                        height="20"
                        style={{ color: "currentColor" }}>
                        <path
                          d="M9 14L4 9l5-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 9h11a4 4 0 1 1 0 8H9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t("planView.backToListShort")}
                    </span>
                  ),
                  onClick: handleBack,
                  type: "normal icon",
              },
          ];

    const pageTitle =
        title ||
        (isNew ? t("recommendView.newTitle") : t("untitled", { ns: "common" }));

    return (
        <Container fluid="md">
            <Row>
                <Col>
                    <div className="patient-page">
                        <TitleWithIcon
                            icon={templateIcon}
                            title={pageTitle}
                            showStatus={false}
                            status={""}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="tab-content block page-block">
                        <Row>
                            <Col>
                                <BlockTitle title={""} buttons={blockButtons} />
                            </Col>
                        </Row>

                        <Row className="">
                            <Col>
                                <div className="page">
                                    <div className="page-content">
                                        {!isEditing && !isNew ? (
                                            <div className="patient-recommendations mt-4">
                                                <div className="recommendation-item mb-4">
                                                    <h5>{title || "—"}</h5>
                                                    <div className="my-3 text-muted small">
                                                        {t("recommendView.typeLabel")}{" "}
                                                        {templateType === "common"
                                                            ? t("recommendView.typeCommon")
                                                            : t("recommendView.typeMy")}
                                                    </div>
                                                    <TiptapViewer
                                                        content={description}
                                                        emptyText={t("recommendView.emptyText")}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="patient-recommendations mt-4">
                                                <div className="mb-3">
                                                    <label className="mb-2">
                                                        {t("recommendView.titleLabel")}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={title}
                                                        onChange={(e) =>
                                                            setTitle(e.target.value)
                                                        }
                                                        placeholder={t("recommendView.titlePlaceholder")}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="mb-2">
                                                        {t("recommendView.typeFieldLabel")}
                                                    </label>
                                                    <select
                                                        className="form-control mb-2"
                                                        value={templateType}
                                                        onChange={(e) =>
                                                            setTemplateType(e.target.value)
                                                        }>
                                                        <option value="common">{t("recommendView.typeCommon")}</option>
                                                        <option value="my">{t("recommendView.typeMy")}</option>
                                                    </select>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="mb-2">
                                                        {t("recommendView.descriptionLabel")}
                                                    </label>
                                                    <TiptapEditor
                                                        value={description}
                                                        onChange={setDescription}
                                                        placeholder={t("recommendView.descriptionPlaceholder")}
                                                        minHeight="200px"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default TreatmentRecommendView;

